import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { apiRoutes } from 'src/app/constants/apiRoutes';
import { AlertService } from 'src/app/shared/services/alert.service';
import { ApiCallMethodsService } from 'src/app/shared/services/api-call-methods.service';
import { AuthService } from 'src/app/authentication/services/auth.service';
import { PaymentPopupComponent } from '../../../main/modules/sell-management/components/payment-popup/payment-popup.component';
import * as moment from 'moment';

@Component({
  selector: 'app-payment-details-drawer',
  templateUrl: './payment-details-drawer.component.html',
  styleUrls: ['./payment-details-drawer.component.scss']
})
export class PaymentDetailsDrawerComponent implements OnInit {
  selectedPayment: any = null;
  activeStatus: string = '';
  auditTrailLogs: any[] = [];
  orgId: any = null;
  notesMap: { [key: string]: Array<{ text: string, time: string }> } = {};
  logsMap: { [key: string]: Array<{ title: string, prevStatus?: string, newStatus?: string, time: string, text: string, isDisputed?: boolean }> } = {};

  constructor(
    public dialogRef: MatDialogRef<PaymentDetailsDrawerComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { invoice: any },
    private apiCallMethodsService: ApiCallMethodsService,
    private authService: AuthService,
    private alertService: AlertService,
    public dialog: MatDialog
  ) {
    this.orgId = this.authService.getId();
  }

  ngOnInit(): void {
    if (this.data && this.data.invoice) {
      this.loadPaymentDetails(this.data.invoice);
    }
  }

  loadPaymentDetails(invoice: any) {
    this.selectedPayment = invoice;
    this.activeStatus = invoice.iqp_status || 'Settled';

    // Call payment details API
    const session = this.authService.getSessionDetail();
    const userId = session?.user?.user_id || '69f32b48c97083cfe298f9eb';
    const orgId = this.orgId !== undefined && this.orgId !== null ? this.orgId : 'null';
    const detailsUrl = apiRoutes.amountPayment + '/' + invoice.iqp_tran_id + '?org_id=' + orgId + '&user_id=' + userId;

    this.apiCallMethodsService.get(detailsUrl).subscribe((response: any) => {
      if (response && response.success && response.data && response.data.length > 0) {
        this.selectedPayment = {
          ...response.data[0],
          contact_name: response.contact_name,
          invoice_number: response.invoice_number,
          contact_first_name: response.contact_name,
          iqp_invoice_number: response.invoice_number
        };
        const statusMap: { [key: number]: string } = {
          100: 'Settled',
          101: 'Verified',
          102: 'Disputed'
        };
        this.activeStatus = statusMap[this.selectedPayment.status] || this.selectedPayment.iqp_status || 'Settled';
        this.selectedPayment.iqp_status = this.activeStatus;
      }
    }, (error: any) => {
      console.error('Error loading payment details', error);
    });

    // Call audit trail API
    this.getAuditTrail(invoice.iqp_tran_id);
  }

  closeDrawer() {
    this.dialogRef.close();
  }

  getAuditTrail(txnId: any) {
    const url = 'admin/payment-audit-trail/' + txnId;
    this.apiCallMethodsService.get(url).subscribe((response: any) => {
      if (response && response.success && response.data) {
        this.auditTrailLogs = response.data;
      } else {
        this.auditTrailLogs = [];
      }
    }, (error: any) => {
      console.error('Error loading payment audit trail', error);
      this.auditTrailLogs = [];
    });
  }

  formatPaymentDate(dateStr: string): string {
    if (!dateStr) return '';

    let date: Date;
    if (dateStr.includes('/')) {
      const [day, month, year] = dateStr.split('/').map(Number);
      date = new Date(year, month - 1, day);
    } else {
      date = new Date(dateStr);
    }

    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  }

  formatAuditTime(isoStr: string): string {
    if (!isoStr) return '';
    const date = new Date(isoStr);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    let h = date.getHours();
    let m: number | string = date.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    m = m < 10 ? '0' + m : m;
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${h}:${m} ${ampm}`;
  }

  getOperationDotClass(log: any): string {
    if (!log) return 'node-dot-slate';

    const op = log.operation;
    if (op === 'Payment recorded' || op === 'payment-recorded') {
      return 'node-dot-indigo';
    }
    if (op === 'Payment Verified' || op === 'payment-verified') {
      return 'node-dot-emerald';
    }
    if (op === 'Payment Flagged As Disputed' || op === 'payment-disputed' || op === 'Payment deleted' || op === 'payment-deleted') {
      return 'node-dot-rose';
    }
    if (op === 'Payment edited' || op === 'updated-payment-record') {
      return 'node-dot-dark';
    }

    return 'node-dot-slate';
  }

  getOperationTitleClass(log: any): string {
    if (!log) return 'node-title';

    const op = log.operation;
    if (op === 'Payment Verified' || op === 'payment-verified') {
      return 'node-title-emerald';
    }
    if (op === 'Payment Flagged As Disputed' || op === 'payment-disputed' || op === 'Payment deleted' || op === 'payment-deleted') {
      return 'node-title-rose';
    }

    return 'node-title';
  }

  formatOperationTitle(operation: string): string {
    if (!operation) return 'Audit Log';
    return operation;
  }

  formatOperationDescription(log: any): string {
    if (!log) return '';
    const name = log.changed_by_name || 'System User';
    const op = log.operation;
    
    if (op === 'payment-recorded' || op === 'Payment recorded') {
      let detailsText = '';
      if (log.new_data) {
        try {
          const newData = JSON.parse(log.new_data);
          const amt = newData.iqp_partly_pmnt_amnt ? parseFloat(newData.iqp_partly_pmnt_amnt).toFixed(2) : '0.00';
          const type = newData.iqp_payment_type || 'N/A';
          detailsText = ` with amount ₹${amt} via ${type}`;
          if (newData.iqp_payment_ref) {
            detailsText += ` (Ref: ${newData.iqp_payment_ref})`;
          }
        } catch (e) {
          console.error('Error parsing new_data in audit log', e);
        }
      }
      return `Payment entry recorded by ${name}${detailsText}.`;
    }
    
    if (op === 'updated-payment-record' || op === 'Payment edited') {
      let changes: string[] = [];
      try {
        const prev = log.previous_data ? JSON.parse(log.previous_data) : {};
        const curr = log.new_data ? JSON.parse(log.new_data) : {};
        
        const normalizeDate = (dStr: any) => {
          if (!dStr) return '';
          const date = new Date(dStr);
          return isNaN(date.getTime()) ? dStr : date.toDateString();
        };

        if (prev.iqp_partly_pmnt_amnt !== curr.iqp_partly_pmnt_amnt) {
          const prevAmt = prev.iqp_partly_pmnt_amnt ? parseFloat(prev.iqp_partly_pmnt_amnt).toFixed(2) : '0.00';
          const currAmt = curr.iqp_partly_pmnt_amnt ? parseFloat(curr.iqp_partly_pmnt_amnt).toFixed(2) : '0.00';
          changes.push(`amount updated from ₹${prevAmt} to ₹${currAmt}`);
        }
        if (prev.iqp_payment_type !== curr.iqp_payment_type) {
          changes.push(`payment type changed from "${prev.iqp_payment_type || 'N/A'}" to "${curr.iqp_payment_type || 'N/A'}"`);
        }
        if (normalizeDate(prev.iqp_paid_date) !== normalizeDate(curr.iqp_paid_date)) {
          const prevDate = prev.iqp_paid_date ? new Date(prev.iqp_paid_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
          const currDate = curr.iqp_paid_date ? new Date(curr.iqp_paid_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A';
          changes.push(`payment date updated from ${prevDate} to ${currDate}`);
        }
        if (prev.iqp_payment_ref !== curr.iqp_payment_ref) {
          changes.push(`reference (UTR) updated from "${prev.iqp_payment_ref || 'None'}" to "${curr.iqp_payment_ref || 'None'}"`);
        }
        if (prev.notes !== curr.notes) {
          changes.push(`notes modified`);
        }
        if (prev.iqp_invoice_number !== curr.iqp_invoice_number) {
          changes.push(`invoice number changed from "${prev.iqp_invoice_number || 'None'}" to "${curr.iqp_invoice_number || 'None'}"`);
        }
      } catch (e) {
        console.error('Error parsing previous/new data in audit log', e);
      }
      
      if (changes.length > 0) {
        return `Payment entry details modified by ${name}: ${changes.join(', ')}.`;
      } else {
        return `Payment entry details modified by ${name}.`;
      }
    }

    if (op === 'Payment Verified' || op === 'payment-verified') {
      return `Payment was verified by ${name}.`;
    }

    if (op === 'Payment Flagged As Disputed' || op === 'payment-disputed') {
      return `Payment was flagged as disputed by ${name}.`;
    }

    if (op === 'Payment deleted' || op === 'payment-deleted') {
      return `Payment entry was deleted by ${name}.`;
    }
    
    return `Operation '${op}' performed by ${name}.`;
  }

  getCurrentTimestamp(): string {
    const date = new Date();
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    let h = date.getHours();
    let m: number | string = date.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    m = m < 10 ? '0' + m : m;
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()} · ${h}:${m} ${ampm}`;
  }

  verifyPayment() {
    if (!this.selectedPayment) return;
    const session = this.authService.getSessionDetail();
    const userId = session?.user?.user_id || '69f32b48c97083cfe298f9eb';
    const orgId = this.orgId !== undefined && this.orgId !== null ? this.orgId : 'null';
    const txnId = this.selectedPayment.iqp_tran_id;

    const date = this.selectedPayment.iqp_paid_date ? new Date(this.selectedPayment.iqp_paid_date) : null;
    const formattedDate = date ? moment(`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`, 'YYYY/MM/DD').format('YYYY/MM/DD') : null;

    const payload = {
      iqp_payment_type: this.selectedPayment.iqp_payment_type || null,
      iqp_payment_ref: this.selectedPayment.iqp_payment_ref || null,
      iqp_paid_date: formattedDate,
      notes: this.selectedPayment.notes || null,
      wo_id: this.selectedPayment.wo_id || null,
      payment_against: this.selectedPayment.payment_against || null,
      iqp_partly_pmnt_amnt: this.selectedPayment.iqp_partly_pmnt_amnt || null,
      status: 101
    };

    const url = `${apiRoutes.amountPayment}/${txnId}?org_id=${orgId}&user_id=${userId}`;
    this.apiCallMethodsService.put(url, payload).subscribe(
      (response: any) => {
        if (response && response.success) {
          this.activeStatus = 'Verified';
          this.selectedPayment.iqp_status = 'Verified';
          this.selectedPayment.status = 101;
          this.alertService.successAlert('Payment verified successfully');
          this.getAuditTrail(txnId);
        } else {
          this.alertService.failureAlert(response?.message || 'Failed to verify payment');
        }
      },
      (error: any) => {
        this.alertService.failureAlert(error?.error?.message || 'An error occurred while verifying the payment');
      }
    );
  }

  flagDisputedPayment() {
    if (!this.selectedPayment) return;
    const session = this.authService.getSessionDetail();
    const userId = session?.user?.user_id || '69f32b48c97083cfe298f9eb';
    const orgId = this.orgId !== undefined && this.orgId !== null ? this.orgId : 'null';
    const txnId = this.selectedPayment.iqp_tran_id;

    const date = this.selectedPayment.iqp_paid_date ? new Date(this.selectedPayment.iqp_paid_date) : null;
    const formattedDate = date ? moment(`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`, 'YYYY/MM/DD').format('YYYY/MM/DD') : null;

    const payload = {
      iqp_payment_type: this.selectedPayment.iqp_payment_type || null,
      iqp_payment_ref: this.selectedPayment.iqp_payment_ref || null,
      iqp_paid_date: formattedDate,
      notes: this.selectedPayment.notes || null,
      wo_id: this.selectedPayment.wo_id || null,
      payment_against: this.selectedPayment.payment_against || null,
      iqp_partly_pmnt_amnt: this.selectedPayment.iqp_partly_pmnt_amnt || null,
      status: 102
    };

    const url = `${apiRoutes.amountPayment}/${txnId}?org_id=${orgId}&user_id=${userId}`;
    this.apiCallMethodsService.put(url, payload).subscribe(
      (response: any) => {
        if (response && response.success) {
          this.activeStatus = 'Disputed';
          this.selectedPayment.iqp_status = 'Disputed';
          this.selectedPayment.status = 102;
          this.alertService.successAlert('Payment flagged as disputed');
          this.getAuditTrail(txnId);
        } else {
          this.alertService.failureAlert(response?.message || 'Failed to flag payment as disputed');
        }
      },
      (error: any) => {
        this.alertService.failureAlert(error?.error?.message || 'An error occurred while flagging the payment');
      }
    );
  }

  postAuditNote(textarea: HTMLTextAreaElement) {
    const noteText = textarea.value.trim();
    if (!noteText || !this.selectedPayment) return;
    
    const txnId = this.selectedPayment.iqp_tran_id;
    const ts = this.getCurrentTimestamp();
    
    if (!this.notesMap[txnId]) {
      this.notesMap[txnId] = [];
    }
    this.notesMap[txnId].push({
      text: noteText,
      time: ts
    });
    
    textarea.value = '';
    this.alertService.successAlert('Audit ledger note saved successfully');
  }

  handleNoteKeydown(event: KeyboardEvent, textarea: HTMLTextAreaElement) {
    if (event.key === 'Enter' && event.shiftKey) {
      event.preventDefault();
      this.postAuditNote(textarea);
    }
  }



  get currentNotes(): Array<{ text: string, time: string }> {
    if (!this.selectedPayment) return [];
    return this.notesMap[this.selectedPayment.iqp_tran_id] || [];
  }
  
  get currentLogs(): Array<{ title: string, prevStatus?: string, newStatus?: string, time: string, text: string, isDisputed?: boolean }> {
    if (!this.selectedPayment) return [];
    return this.logsMap[this.selectedPayment.iqp_tran_id] || [];
  }

  openEditPaymentPopup(event: any, invoice?: any) {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    const inv = invoice || event;
    if (!inv) return;
    const dialogRef = this.dialog.open(PaymentPopupComponent, {
      minWidth: '10vw', maxWidth: '50vw', maxHeight: '80vh', autoFocus: false,
      data: {
        transactionId: inv.iqp_tran_id,
        invNumber: inv.iqp_invoice_number,
        invoiceId: inv.iqp_id,
        amount: inv.iqp_partly_pmnt_amnt
      }
    });
    dialogRef.afterClosed().subscribe(() => {
      this.loadPaymentDetails(inv);
    });
  }

  deletePayment(event: any, invoice?: any) {
    if (event && typeof event.stopPropagation === 'function') {
      event.stopPropagation();
    }
    const inv = invoice || event;
    if (!inv) return;
    const session = this.authService.getSessionDetail();
    const userId = session?.user?.user_id || '69f32b48c97083cfe298f9eb';
    const orgId = this.orgId !== undefined && this.orgId !== null ? this.orgId : 'null';
    const url = `${apiRoutes.amountPayment}/${inv.iqp_tran_id}?org_id=${orgId}&user_id=${userId}`;

    this.alertService.confirmAlert('You want to delete this payment').subscribe((userChoice: any) => {
      if (userChoice === 'confirm') {
        const date = inv.iqp_paid_date ? new Date(inv.iqp_paid_date) : null;
        const formattedDate = date ? moment(`${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`, 'YYYY/MM/DD').format('YYYY/MM/DD') : null;

        const payload = {
          iqp_payment_type: inv.iqp_payment_type || null,
          iqp_payment_ref: inv.iqp_payment_ref || null,
          iqp_paid_date: formattedDate,
          notes: inv.notes || null,
          wo_id: inv.wo_id || null,
          payment_against: inv.payment_against || null,
          iqp_partly_pmnt_amnt: inv.iqp_partly_pmnt_amnt || null,
          status: 100,
          is_deleted: true
        };
        this.apiCallMethodsService.put(url, payload).subscribe(
          (response: any) => {
            if (response && response.success) {
              this.alertService.successAlert('Payment deleted successfully');
              this.dialogRef.close('deleted');
            } else {
              this.alertService.failureAlert(response?.message || 'Failed to delete payment');
            }
          },
          (error: any) => {
            this.alertService.failureAlert(error?.error?.message || 'An error occurred while deleting the payment');
          }
        );
      }
    });
  }
}
